import { Stack, Typography } from '@mui/material';
import { Routes } from 'enums';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { To, useLocation, useNavigate, useSearchParams } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import PaiActionText from '../../ui/base-components/pai-action-text';
import PaiButton from '../../ui/base-components/pai-button';
import { ProjectWrapperKeys } from '../profile-generator/use-query-project-wrapper';
import { LinkedinSearchProject } from './linkedin-profile-finder.types';
import SearchProjectItem from './search-project-item';

interface LocationState {
  from?: string;
  query?: string;
}

interface Props {
  urlFinderProjectName: string;
  urlFinderProjects: LinkedinSearchProject[];
  startSearchLoading: boolean;

  startSearch(project: LinkedinSearchProject): () => void;

  restartSearch(project: LinkedinSearchProject): () => void;

  downloadSearchResults(id: string): () => void;
}

const FindLinkedinUrls: React.FC<Props> = ({
  urlFinderProjectName,
  urlFinderProjects,
  startSearchLoading,
  startSearch,
  restartSearch,
  downloadSearchResults,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as LocationState | null;
  const redirectPath = locationState?.from && locationState?.query ? `${locationState.from}${locationState.query}` : undefined;

  const goToPersonaGenerator = () => {
    const querySearchProjectFolder = searchParams.get(ProjectWrapperKeys.URL_FINDER_PROJECT);

    if (!_.isNil(querySearchProjectFolder)) {
      navigate(`${Routes.PERSONA_GENERATOR}?${ProjectWrapperKeys.URL_FINDER_PROJECT}=${querySearchProjectFolder}`);
    }
  };

  const personaGenerationPossible = () => {
    return urlFinderProjects.some((project) => project.analysisFinished);
  };

  return (
    <div className={classes.container}>
      <div className={classes.action}>
        <PaiActionText
          text={!_.isNil(redirectPath) ? t('backToCampaign') : t('back')}
          backArrow={true}
          action={() => navigate(redirectPath ?? -1 as To)}
        />
      </div>
      <Typography className={classes.title}>{t('manageUrlFinderProject', { projectName: urlFinderProjectName })}</Typography>
      <Typography className={classes.text}>{t('urlFinderProjectsText')}</Typography>
      <Stack className={classes.projects} direction={'column'}>
        {urlFinderProjects.map((project, index) => (
          <SearchProjectItem
            key={project.id}
            project={project}
            isEven={index % 2 === 0}
            startSearchLoading={startSearchLoading}
            startSearch={startSearch}
            restartSearch={restartSearch}
            downloadSearchResults={downloadSearchResults}
          />
        ))}
      </Stack>
      <div className={classes.buttonContainer}>
        <PaiButton text={t('createPersonaProject')} action={goToPersonaGenerator} disabled={!personaGenerationPossible()} />
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'relative',
    width: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '20px 16px',
    height: 'calc(100% - 16px - 20px)',
  },
  action: {
    position: 'absolute',
    left: 0,
    top: '-38px',
  },
  title: {
    fontSize: theme.typography.pxToRem(18),
    marginBottom: '16px',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    marginBottom: '16px',
  },
  projects: {
    border: `2px solid ${theme.palette.backgrounds.main}`,
    maxHeight: '425px',
    overflow: 'auto',
    overflowX: 'hidden',
  },
  cellTitle: {
    fontSize: theme.typography.pxToRem(12),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '24px',
  },
}));

export default FindLinkedinUrls;
